import { useCallback, useEffect, useRef } from "react";

const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

async function waitForTurnstileReady(checkReady, timeoutMs = 8000, stepMs = 120) {
  const startedAt = Date.now();
  while (!checkReady()) {
    if (Date.now() - startedAt >= timeoutMs) return false;
    await new Promise((resolve) => window.setTimeout(resolve, stepMs));
  }
  return true;
}

export default function useTurnstileBotCheck({ siteKey, enabled = true }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const resolveRef = useRef(null);
  const rejectRef = useRef(null);
  const timeoutRef = useRef(null);

  const clearPending = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    resolveRef.current = null;
    rejectRef.current = null;
  }, []);

  useEffect(() => {
    if (!enabled || !siteKey || typeof window === "undefined") return undefined;

    const initializeWidget = () => {
      if (!window.turnstile || !containerRef.current || widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        // Some Turnstile widget/site-key configurations reject `invisible`.
        // Use a supported size and keep explicit execution mode.
        size: "flexible",
        execution: "execute",
        appearance: "execute",
        callback: (token) => {
          const resolver = resolveRef.current;
          clearPending();
          if (resolver) resolver(token);
        },
        "error-callback": () => {
          const rejecter = rejectRef.current;
          clearPending();
          if (rejecter) rejecter(new Error("Security verification failed. Please try again."));
        },
        "timeout-callback": () => {
          const rejecter = rejectRef.current;
          clearPending();
          if (rejecter) rejecter(new Error("Security verification timed out. Please try again."));
        },
      });
    };
    const attemptInitialize = () => {
      try {
        initializeWidget();
      } catch {
        // Ignore transient Turnstile init errors; getToken will surface if still unavailable.
      }
    };
    const intervalId = window.setInterval(() => {
      if (widgetIdRef.current !== null) {
        window.clearInterval(intervalId);
        return;
      }
      attemptInitialize();
    }, 250);

    if (window.turnstile) {
      attemptInitialize();
      return () => {
        window.clearInterval(intervalId);
      };
    }

    let scriptEl = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
    let ownsScript = false;
    if (!scriptEl) {
      scriptEl = document.createElement("script");
      scriptEl.src = TURNSTILE_SCRIPT_SRC;
      scriptEl.async = true;
      scriptEl.defer = true;
      document.head.appendChild(scriptEl);
      ownsScript = true;
    }

    scriptEl.addEventListener("load", attemptInitialize);

    return () => {
      window.clearInterval(intervalId);
      scriptEl.removeEventListener("load", attemptInitialize);
      if (ownsScript && scriptEl.parentNode) {
        // Keep the script in DOM for reuse by other pages.
      }
    };
  }, [clearPending, enabled, siteKey]);

  useEffect(() => () => clearPending(), [clearPending]);

  const getToken = useCallback(
    (action = "submit") =>
      new Promise(async (resolve, reject) => {
        if (!enabled) {
          resolve("");
          return;
        }
        if (!siteKey) {
          if (process.env.NODE_ENV === "production") {
            reject(new Error("Security verification is not configured. Please contact support."));
            return;
          }
          resolve("");
          return;
        }
        if (typeof window === "undefined") {
          reject(new Error("Security verification is still loading. Please retry."));
          return;
        }
        if (!window.turnstile || widgetIdRef.current === null) {
          const ready = await waitForTurnstileReady(
            () => Boolean(window.turnstile && widgetIdRef.current !== null),
            8000
          );
          if (!ready) {
            reject(new Error("Security verification is still loading. Please retry."));
            return;
          }
        }

        clearPending();
        resolveRef.current = resolve;
        rejectRef.current = reject;
        timeoutRef.current = window.setTimeout(() => {
          const rejecter = rejectRef.current;
          clearPending();
          if (rejecter) rejecter(new Error("Security verification timed out. Please retry."));
        }, 10000);

        try {
          // Prefer execute-first. Reset can trigger noisy cross-frame warnings in some browsers.
          window.turnstile.execute(widgetIdRef.current, { action });
        } catch (error) {
          try {
            window.turnstile.reset(widgetIdRef.current);
            window.turnstile.execute(widgetIdRef.current, { action });
          } catch (fallbackError) {
            clearPending();
            reject(
              fallbackError instanceof Error
                ? fallbackError
                : new Error("Security verification failed. Please retry.")
            );
          }
        }
      }),
    [clearPending, enabled, siteKey]
  );

  return { containerRef, getToken };
}
